import { createContext, FC, useContext, useEffect } from 'react'
import type { MediaStatus } from './model/media-status'

type Position = {
	positionMs: number
	durationMs: number
	playing: boolean
	/** `Date.now()` when measured, to extrapolate between reports. */
	at: number
}

/**
 * What kind of player reports. A recording also says whether it has its file
 * (`status`): the console mirrors that, because a recording that failed to
 * load looks like one that is ready to the one who cannot see the player. A
 * film needs no status: the console watches it in a player of its own, which
 * shows the film's picture as long as the projector's does (`cued`: loaded,
 * never played).
 */
type Player =
	| { kind: 'youtube'; cued: boolean }
	| { kind: 'audio'; status: MediaStatus }

/** How far a film or recording on the stage has played. */
export type MediaProgress = Position & Player

export type MediaProgressSink = (progress: MediaProgress | null) => void

const MediaProgressContext = createContext<MediaProgressSink>(() => undefined)

/**
 * Who hears where the player on this stage is. Nobody, unless presenter mode
 * plays the media in the presentation window and the console shows its
 * position.
 */
export const ProvideMediaProgressSink: FC<{ sink: MediaProgressSink }> = ({
	sink,
	children,
}) => (
	<MediaProgressContext.Provider value={sink}>
		{children}
	</MediaProgressContext.Provider>
)

/** Who hears this stage's player, for a player that reports by itself. */
export const useMediaProgressSink = (): MediaProgressSink =>
	useContext(MediaProgressContext)

/**
 * A recording reports its position and status; `null` once it is gone. A
 * film reports itself (`useReportYouTubeProgress`), from its player.
 */
export const useReportMediaProgress = (
	report: Omit<Position, 'at'> & { kind: 'audio'; status: MediaStatus }
): void => {
	const sink = useContext(MediaProgressContext)
	const { status, positionMs, durationMs, playing } = report
	useEffect(() => {
		sink({
			kind: 'audio',
			status,
			positionMs,
			durationMs,
			playing,
			at: Date.now(),
		})
	}, [sink, status, positionMs, durationMs, playing])
	useEffect(() => () => sink(null), [sink])
}
